import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Autocomplete,
  Divider,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Replay as ResetIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const checklistTemplate = [
  { key: 'farol', label: 'Farol' },
  { key: 'pisca', label: 'Pisca' },
  { key: 'lanterna', label: 'Lanterna' },
  { key: 'luz_freio', label: 'Luz de freio' },
  { key: 'ar', label: 'Ar condicionado' },
  { key: 'retrovisor', label: 'Retrovisor' },
  { key: 'oleo', label: 'Nível de óleo' },
  { key: 'vidros', label: 'Vidros' },
  { key: 'estofamento', label: 'Estofamento' },
  { key: 'bateria', label: 'Bateria' },
  { key: 'documentos', label: 'Documentos' },
  { key: 'gps', label: 'GPS' },
  { key: 'som', label: 'Som' },
  { key: 'pneus', label: 'Pneus' },
  { key: 'freios', label: 'Freios' },
];

const buildChecklist = () => checklistTemplate.map((item) => ({
  ...item,
  status: 'ok',
  observacao: '',
}));

const buildFormData = () => ({
  veiculo_id: null,
  veiculo_texto: '',
  cliente_id: null,
  data_avaliacao: new Date().toISOString().split('T')[0],
  combustivel_entrada: 50,
  combustivel_saida: 50,
  valor_fipe: '',
  valor_avaliacao: '',
  observacoes: '',
  checklist: buildChecklist(),
  danos: [],
});

export default function Avaliacoes() {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [formData, setFormData] = useState(buildFormData());
  const [selectedAvaliacaoId, setSelectedAvaliacaoId] = useState(null);
  const [selectedVeiculo, setSelectedVeiculo] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [mapaUrl, setMapaUrl] = useState('');
  const [historicoSearch, setHistoricoSearch] = useState('');
  const mapaRef = useRef(null);

  useEffect(() => {
    loadAvaliacoes();
    loadVeiculos();
    loadClientes();
    loadMapaDanos();
  }, []);

  const loadAvaliacoes = async () => {
    try {
      const response = await api.get('/avaliacoes');
      setAvaliacoes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      toast.error('Erro ao carregar avaliações');
    }
  };

  const loadVeiculos = async () => {
    try {
      const response = await api.get('/veiculos', { params: { limit: 1000 } });
      const data = response.data.veiculos || response.data || [];
      setVeiculos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
      setVeiculos([]);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes', { params: { limit: 1000 } });
      const data = response.data.clientes || response.data || [];
      setClientes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClientes([]);
    }
  };

  const loadMapaDanos = async () => {
    try {
      const response = await api.get('/branding/avaliacao-mapa');
      setMapaUrl(response.data.url || '');
    } catch (error) {
      console.error('Erro ao carregar mapa de danos:', error);
      setMapaUrl('');
    }
  };

  const checklistSummary = useMemo(() => {
    const counts = { ok: 0, atencao: 0, nao_ok: 0 };
    formData.checklist.forEach((item) => {
      if (counts[item.status] !== undefined) {
        counts[item.status] += 1;
      }
    });
    return counts;
  }, [formData.checklist]);

  const handleChecklistStatusChange = (index, value) => {
    if (!value) return;
    const checklist = [...formData.checklist];
    checklist[index] = { ...checklist[index], status: value };
    setFormData({ ...formData, checklist });
  };

  const handleChecklistObsChange = (index, value) => {
    const checklist = [...formData.checklist];
    checklist[index] = { ...checklist[index], observacao: value };
    setFormData({ ...formData, checklist });
  };

  const handleMapaClick = (event) => {
    if (!mapaRef.current) return;
    const rect = mapaRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const novoDano = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      x: Number(x.toFixed(1)),
      y: Number(y.toFixed(1)),
      tipo: 'arranhado',
      descricao: '',
    };
    setFormData((prev) => ({ ...prev, danos: [...prev.danos, novoDano] }));
  };

  const handleDanoDescricaoChange = (id, value) => {
    const danos = formData.danos.map((dano) => (
      dano.id === id ? { ...dano, descricao: value } : dano
    ));
    setFormData({ ...formData, danos });
  };

  const handleDanoTipoChange = (id, value) => {
    const danos = formData.danos.map((dano) => (
      dano.id === id ? { ...dano, tipo: value } : dano
    ));
    setFormData({ ...formData, danos });
  };

  const handleRemoveDano = (id) => {
    const danos = formData.danos.filter((dano) => dano.id !== id);
    setFormData({ ...formData, danos });
  };

  const handleReset = () => {
    setFormData(buildFormData());
    setSelectedVeiculo(null);
    setSelectedCliente(null);
    setSelectedAvaliacaoId(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        valor_fipe: formData.valor_fipe !== '' ? parseFloat(formData.valor_fipe) : null,
        valor_avaliacao: formData.valor_avaliacao !== '' ? parseFloat(formData.valor_avaliacao) : null,
      };

      if (selectedAvaliacaoId) {
        await api.put(`/avaliacoes/${selectedAvaliacaoId}`, payload);
        toast.success('Avaliação atualizada');
      } else {
        await api.post('/avaliacoes', payload);
        toast.success('Avaliação criada');
      }

      handleReset();
      loadAvaliacoes();
    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
      toast.error('Erro ao salvar avaliação');
    }
  };

  const handleEdit = (avaliacao) => {
    setSelectedAvaliacaoId(avaliacao.id);
    const checklist = Array.isArray(avaliacao.checklist) && avaliacao.checklist.length > 0
      ? avaliacao.checklist
      : buildChecklist();
    setFormData({
      veiculo_id: avaliacao.veiculo_id || null,
      veiculo_texto: avaliacao.veiculo_texto || '',
      cliente_id: avaliacao.cliente_id || null,
      data_avaliacao: avaliacao.data_avaliacao
        ? new Date(avaliacao.data_avaliacao).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      combustivel_entrada: avaliacao.combustivel_entrada ?? 50,
      combustivel_saida: avaliacao.combustivel_saida ?? 50,
      valor_fipe: avaliacao.valor_fipe || '',
      valor_avaliacao: avaliacao.valor_avaliacao || '',
      observacoes: avaliacao.observacoes || '',
      checklist,
      danos: Array.isArray(avaliacao.danos) ? avaliacao.danos : [],
    });
    setSelectedVeiculo(veiculos.find((v) => v.id === avaliacao.veiculo_id) || null);
    setSelectedCliente(clientes.find((c) => c.id === avaliacao.cliente_id) || null);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/avaliacoes/${id}`);
      toast.success('Avaliação removida');
      loadAvaliacoes();
    } catch (error) {
      console.error('Erro ao deletar avaliação:', error);
      toast.error('Erro ao deletar avaliação');
    }
  };

  const downloadPdf = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    window.open(fullUrl, '_blank');
  };

  const handleDownloadPdf = async (id) => {
    try {
      const response = await api.get(`/avaliacoes/${id}/pdf`);
      if (response.data.url) {
        downloadPdf(response.data.url);
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const avaliacoesFiltradas = useMemo(() => {
    if (!historicoSearch.trim()) return avaliacoes;
    const termo = historicoSearch.toLowerCase();
    return avaliacoes.filter((avaliacao) => {
      const veiculo = `${avaliacao.veiculo?.marca || ''} ${avaliacao.veiculo?.modelo || ''}`.toLowerCase();
      const cliente = (avaliacao.cliente?.nome || '').toLowerCase();
      return veiculo.includes(termo) || cliente.includes(termo);
    });
  }, [avaliacoes, historicoSearch]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Avaliação de veículos</Typography>
        <Button
          variant="outlined"
          startIcon={<ResetIcon />}
          onClick={handleReset}
        >
          Nova avaliação
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Veículo e cliente são opcionais. Se não selecionar, a avaliação será salva mesmo assim.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={veiculos}
              value={selectedVeiculo}
              onChange={(event, value) => {
                setSelectedVeiculo(value);
                setFormData({ ...formData, veiculo_id: value?.id || null, veiculo_texto: '' });
              }}
              getOptionLabel={(option) => `${option.marca || ''} ${option.modelo || ''} ${option.placa ? `- ${option.placa}` : ''}`.trim()}
              renderInput={(params) => (
                <TextField {...params} label="Veículo (selecionar existente)" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={clientes}
              value={selectedCliente}
              onChange={(event, value) => {
                setSelectedCliente(value);
                setFormData({ ...formData, cliente_id: value?.id || null });
              }}
              getOptionLabel={(option) => option.nome || ''}
              renderInput={(params) => (
                <TextField {...params} label="Cliente (opcional)" fullWidth />
              )}
            />
          </Grid>
          {!selectedVeiculo && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ou descreva o veículo manualmente (marca, modelo, ano...)"
                value={formData.veiculo_texto}
                onChange={(e) => setFormData({ ...formData, veiculo_texto: e.target.value })}
                placeholder="Ex: Toyota Corolla 2020, Honda Civic 2019..."
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <TextField
              label="Data"
              type="date"
              value={formData.data_avaliacao}
              onChange={(event) => setFormData({ ...formData, data_avaliacao: event.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Valor FIPE (R$) — opcional"
              type="number"
              value={formData.valor_fipe}
              onChange={(e) => setFormData({ ...formData, valor_fipe: e.target.value })}
              inputProps={{ step: '0.01', min: '0' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Valor após avaliação (R$) — opcional"
              type="number"
              value={formData.valor_avaliacao}
              onChange={(e) => setFormData({ ...formData, valor_avaliacao: e.target.value })}
              inputProps={{ step: '0.01', min: '0' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>Combustível entrada</Typography>
            <Slider
              value={formData.combustivel_entrada}
              onChange={(event, value) => setFormData({ ...formData, combustivel_entrada: value })}
              valueLabelDisplay="auto"
              step={5}
              marks={[0, 25, 50, 75, 100].map((value) => ({ value, label: `${value}%` }))}
              min={0}
              max={100}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>Combustível saída</Typography>
            <Slider
              value={formData.combustivel_saida}
              onChange={(event, value) => setFormData({ ...formData, combustivel_saida: value })}
              valueLabelDisplay="auto"
              step={5}
              marks={[0, 25, 50, 75, 100].map((value) => ({ value, label: `${value}%` }))}
              min={0}
              max={100}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>Checklist</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`OK: ${checklistSummary.ok}`} color="success" size="small" />
          <Chip label={`Atenção: ${checklistSummary.atencao}`} color="warning" size="small" />
          <Chip label={`Não ok: ${checklistSummary.nao_ok}`} color="error" size="small" />
        </Box>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {[0, 1].map((col) => {
              const start = col === 0 ? 0 : Math.ceil(formData.checklist.length / 2);
              const end = col === 0 ? Math.ceil(formData.checklist.length / 2) : formData.checklist.length;
              return (
                <Grid item xs={12} md={6} key={col}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formData.checklist.slice(start, end).map((item, offset) => {
                      const index = start + offset;
                      const showObs = item.status !== 'ok' || !!item.observacao;
                      return (
                        <Box key={item.key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="subtitle1">{item.label}</Typography>
                            <ToggleButtonGroup
                              value={item.status}
                              exclusive
                              onChange={(event, value) => handleChecklistStatusChange(index, value)}
                              size="small"
                            >
                              <ToggleButton value="ok" color="success">OK</ToggleButton>
                              <ToggleButton value="atencao" color="warning">Atenção</ToggleButton>
                              <ToggleButton value="nao_ok" color="error">Não ok</ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                          {showObs && (
                            <TextField
                              label="Observação"
                              value={item.observacao}
                              onChange={(event) => handleChecklistObsChange(index, event.target.value)}
                              fullWidth
                              multiline
                              minRows={1}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>Mapa de danos (toque para marcar)</Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            ref={mapaRef}
            onClick={handleMapaClick}
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: 520, md: 640 },
              border: '1px dashed rgba(255,255,255,0.3)',
              borderRadius: 2,
              background: mapaUrl
                ? `url(${mapaUrl}) center/contain no-repeat`
                : 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
              cursor: 'crosshair',
            }}
          >
            {!mapaUrl && (
              <svg
                viewBox="0 0 800 300"
                width="100%"
                height="100%"
                style={{ position: 'absolute', left: 0, top: 0 }}
              >
                <rect x="120" y="90" width="560" height="120" rx="40" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                <rect x="250" y="60" width="300" height="70" rx="30" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                <circle cx="240" cy="230" r="26" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
                <circle cx="560" cy="230" r="26" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
              </svg>
            )}
            {formData.danos.map((dano, index) => (
              <Box
                key={dano.id}
                sx={{
                  position: 'absolute',
                  left: `${dano.x}%`,
                  top: `${dano.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#d62828',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  boxShadow: '0 0 0 2px rgba(0,0,0,0.4)',
                }}
              >
                {index + 1}
              </Box>
            ))}
          </Box>
        </Paper>

        <Grid container spacing={2}>
          {formData.danos.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Nenhum dano marcado.</Typography>
            </Grid>
          )}
          {formData.danos.map((dano, index) => (
            <Grid item xs={12} md={6} key={dano.id}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2">Dano {index + 1}</Typography>
                <TextField
                  select
                  label="Tipo"
                  value={dano.tipo || 'arranhado'}
                  onChange={(event) => handleDanoTipoChange(dano.id, event.target.value)}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  <MenuItem value="arranhado">Arranhado</MenuItem>
                  <MenuItem value="amassado">Amassado</MenuItem>
                  <MenuItem value="piques">Piques</MenuItem>
                  <MenuItem value="trincado">Trincado</MenuItem>
                  <MenuItem value="quebrado">Quebrado</MenuItem>
                  <MenuItem value="falta">Falta</MenuItem>
                </TextField>
                <TextField
                  label="Descrição"
                  value={dano.descricao}
                  onChange={(event) => handleDanoDescricaoChange(dano.id, event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mt: 1 }}
                />
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => handleRemoveDano(dano.id)}
                  sx={{ mt: 1 }}
                >
                  Remover dano
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <TextField
          label="Observações gerais"
          value={formData.observacoes}
          onChange={(event) => setFormData({ ...formData, observacoes: event.target.value })}
          fullWidth
          multiline
          minRows={4}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <Button variant="outlined" startIcon={<ResetIcon />} onClick={handleReset}>
            Limpar
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
            Salvar avaliação
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Histórico de avaliações</Typography>
        <TextField
          fullWidth
          size="small"
          label="Buscar por veículo ou cliente"
          value={historicoSearch}
          onChange={(e) => setHistoricoSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Veículo</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>FIPE</TableCell>
              <TableCell>Valor Avaliado</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {avaliacoesFiltradas.map((avaliacao) => (
              <TableRow key={avaliacao.id} hover>
                <TableCell>
                  {avaliacao.data_avaliacao
                    ? new Date(avaliacao.data_avaliacao).toLocaleDateString('pt-BR')
                    : '-'}
                </TableCell>
                <TableCell>
                  {avaliacao.veiculo
                    ? `${avaliacao.veiculo.marca || ''} ${avaliacao.veiculo.modelo || ''}`.trim()
                    : avaliacao.veiculo_texto || '-'}
                </TableCell>
                <TableCell>{avaliacao.cliente?.nome || '-'}</TableCell>
                <TableCell>
                  {avaliacao.valor_fipe ? `R$ ${parseFloat(avaliacao.valor_fipe).toFixed(2)}` : '-'}
                </TableCell>
                <TableCell>
                  {avaliacao.valor_avaliacao ? `R$ ${parseFloat(avaliacao.valor_avaliacao).toFixed(2)}` : '-'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(avaliacao)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDownloadPdf(avaliacao.id)}>
                    <PdfIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(avaliacao.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {avaliacoesFiltradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>Nenhuma avaliação encontrada.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}



